import { Alert, Button, EmptyState, LoadingState, Modal, Textarea, useToast } from '@orbind/ui'
import { useState } from 'react'
import {
  useDesenvolvedoresDoConsole,
  useRevisarDesenvolvedor,
  type DesenvolvedorNoConsole,
  type StatusDeDesenvolvedor,
} from '../plugin/usePortal.ts'
import '../../pages/portal-dev.css'

/**
 * Os desenvolvedores cadastrados, no Console
 * (SPEC-20260929-2025-plugin-portal-do-desenvolvedor).
 *
 * É a fila de trabalho do super admin: quem pediu para publicar, e o que cada
 * um já publicou. Os pendentes vêm primeiro porque são o que exige ação —
 * uma lista ordenada por data deixaria a decisão pendente no meio do histórico.
 *
 * Recusar e suspender EXIGEM motivo, e o motivo aparece para a própria pessoa
 * no portal dela. Um "não" sem razão gera uma conversa por fora para descobrir
 * o óbvio.
 */

const STATUS: { id: StatusDeDesenvolvedor | 'todos'; rotulo: string }[] = [
  { id: 'todos', rotulo: 'Todos' },
  { id: 'pendente', rotulo: 'Aguardando' },
  { id: 'aprovado', rotulo: 'Aprovados' },
  { id: 'recusado', rotulo: 'Recusados' },
  { id: 'suspenso', rotulo: 'Suspensos' },
]

const ROTULO: Record<StatusDeDesenvolvedor, string> = {
  pendente: 'Aguardando aprovação',
  aprovado: 'Aprovado',
  recusado: 'Recusado',
  suspenso: 'Suspenso',
}

export function DesenvolvedoresSection() {
  const [filtro, setFiltro] = useState<StatusDeDesenvolvedor | 'todos'>('todos')
  const consulta = useDesenvolvedoresDoConsole(filtro === 'todos' ? undefined : filtro)
  const revisar = useRevisarDesenvolvedor()
  const toast = useToast()

  /** Quem está sendo recusado ou suspenso — os dois que pedem motivo. */
  const [decisao, setDecisao] = useState<{ dev: DesenvolvedorNoConsole; status: StatusDeDesenvolvedor } | null>(null)
  const [motivo, setMotivo] = useState('')

  const decidir = (dev: DesenvolvedorNoConsole, status: StatusDeDesenvolvedor) => {
    if (status === 'recusado' || status === 'suspenso') {
      setDecisao({ dev, status })
      setMotivo('')
      return
    }
    revisar.mutate(
      { id: dev.id, status },
      {
        onSuccess: () => toast.push({ tone: 'success', message: `${dev.displayName} foi aprovado.` }),
        onError: (e) =>
          toast.push({
            tone: 'danger',
            message: e instanceof Error ? e.message : 'Não foi possível salvar a decisão.',
          }),
      },
    )
  }

  const confirmar = () => {
    if (!decisao) return
    revisar.mutate(
      { id: decisao.dev.id, status: decisao.status, motivo: motivo.trim() },
      {
        onSuccess: () => {
          setDecisao(null)
          toast.push({
            tone: 'success',
            message: `${decisao.dev.displayName} — ${ROTULO[decisao.status].toLowerCase()}.`,
          })
        },
        onError: (e) =>
          toast.push({
            tone: 'danger',
            message: e instanceof Error ? e.message : 'Não foi possível salvar a decisão.',
          }),
      },
    )
  }

  const devs = consulta.data?.desenvolvedores ?? []

  return (
    <div className="pdv" style={{ padding: 0, maxWidth: 'none' }}>
      <header className="pdv__head">
        <h2 className="pdv__title" style={{ fontSize: 19 }}>
          Desenvolvedores
        </h2>
        <p className="pdv__sub">
          Quem se cadastrou para publicar plugins nesta instalação. Só quem está aprovado publica — e é a você que
          o Orbind recorre quando um plugin de terceiro causa problema.
        </p>
      </header>

      <div className="cons-chips">
        {STATUS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`ob-filterchip${filtro === s.id ? ' ob-filterchip--on' : ''}`}
            onClick={() => setFiltro(s.id)}
          >
            {s.rotulo}
          </button>
        ))}
      </div>

      {consulta.isPending ? (
        <LoadingState rows={4} label="Carregando os desenvolvedores" />
      ) : consulta.isError ? (
        <Alert
          tone="danger"
          title="Não foi possível carregar"
          action={
            <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
              Tentar de novo
            </Button>
          }
        >
          {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
        </Alert>
      ) : devs.length === 0 ? (
        <EmptyState
          icon="fas fa-user-astronaut"
          title="Nenhum desenvolvedor por aqui"
          description="Quem quiser publicar um plugin se cadastra no portal do desenvolvedor, e aparece nesta lista."
        />
      ) : (
        <ul className="pdv__lista">
          {devs.map((d) => (
            <li key={d.id} className="pdv__card">
              <div className="pdv__card-main">
                <span className="pdv__nome">{d.displayName}</span>
                <span className="pdv__meta">
                  {d.memberName} · {d.contactEmail}
                </span>
                {d.website ? <span className="pdv__meta">{d.website}</span> : null}
                <span className={`pdv__status pdv__status--${d.status}`}>
                  <i
                    className={
                      d.status === 'aprovado'
                        ? 'fas fa-check-circle'
                        : d.status === 'pendente'
                          ? 'fas fa-clock'
                          : 'fas fa-exclamation-circle'
                    }
                    aria-hidden
                  />
                  {ROTULO[d.status]}
                </span>
                <span className="pdv__meta">
                  {d.plugins} {d.plugins === 1 ? 'plugin publicado' : 'plugins publicados'}
                </span>
                {d.statusReason ? <span className="pdv__motivo">{d.statusReason}</span> : null}
              </div>

              <div className="pdv__acoes" style={{ flexDirection: 'column' }}>
                {d.status !== 'aprovado' ? (
                  <Button
                    variant="primary"
                    size="sm"
                    icon="fas fa-check"
                    disabled={revisar.isPending}
                    onClick={() => decidir(d, 'aprovado')}
                  >
                    Aprovar
                  </Button>
                ) : null}
                {d.status === 'pendente' ? (
                  <Button size="sm" icon="fas fa-times" disabled={revisar.isPending} onClick={() => decidir(d, 'recusado')}>
                    Recusar
                  </Button>
                ) : null}
                {d.status === 'aprovado' ? (
                  <Button size="sm" icon="fas fa-ban" disabled={revisar.isPending} onClick={() => decidir(d, 'suspenso')}>
                    Suspender
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={decisao !== null}
        onClose={() => setDecisao(null)}
        title={decisao ? `${ROTULO[decisao.status]}: ${decisao.dev.displayName}` : ''}
        width={480}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDecisao(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={revisar.isPending || motivo.trim() === ''}
              onClick={confirmar}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <div className="pdv__campo">
          <span className="pdv__rotulo">Por quê? *</span>
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={4} />
          <span className="pdv__dica">
            {/* é o texto que a pessoa lê no portal dela — e o que permite corrigir */}
            A pessoa vê este texto no portal dela. Diga o que precisa mudar.
          </span>
        </div>
      </Modal>
    </div>
  )
}
